import type { GateContext, Lesson } from '../types';
import { f, lastSample } from './helpers';

const before = (ctx: GateContext) => ctx.sample('m2-open', 2.9);
const after = (ctx: GateContext) => lastSample(ctx, 'm2-open');

export const m2: Lesson = {
  id: 'm2',
  number: 2,
  title: 'Predict versus correct',
  summary: 'In this module, you will discover why reliable flight needs two complementary paths: feedforward to anticipate the thrust required, and feedback to measure and fix unexpected disturbances.',
  objectives: [
    'Identify the feedforward and feedback paths in a block diagram.',
    'Explain why a predicted thrust fails to correct an unexpected load.',
    'Identify the measurement feedback needs.',
  ],
  controls: ['manual', 'feedforward', 'contributions'],
  controllerKinds: ['open-loop', 'manual', 'p'],
  sections: [
    {
      id: 'm2-contract',
      title: 'One thrust rule for every controller',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'In production flight software (such as PX4 or ArduPilot), flight controllers follow a strict architectural contract: motor commands are structured as a baseline model prediction (feedforward) plus an active error correction (feedback). This modular design keeps flight code clean and reliable across all flight modes, while ensuring motor requests never violate the physical limits of the hardware.',
        },
        {
          type: 'p',
          text: 'Every controller in this tutorial follows this exact three-step sequence to determine rotor thrust:',
        },
        {
          type: 'math',
          tex: '\\begin{aligned} \\term{Tcmd}{T_{cmd}} &= \\term{feedforward}{T_{ff}} + \\term{ufb}{u_{fb}} \\\\ \\term{T}{T} &= \\term{clamp}{\\operatorname{clamp}}(\\term{Tcmd}{T_{cmd}},\\ 0,\\ \\term{Tmax}{T_{max}}) \\\\ \\term{u}{u} &= \\term{T}{T} - \\term{T0}{T_0} \\end{aligned}',
          aria: 'T command equals T f f plus u f b. Applied thrust T is T command clamped to between 0 and T max. Thrust deviation u is T minus T sub zero.',
        },
        {
          type: 'list',
          items: [
            '**Commanded thrust** ($\\term{Tcmd}{T_{cmd}}$): Sum of the predicted baseline {{feedforward|$T_{ff}$}} and the feedback correction {{ufb|$u_{fb}$}}.',
            '**Actuator saturation clamp** ($\\term{T}{T}$): Real propellers cannot push downward ($T \\ge 0$) and max out at a ceiling ($T_{max} = 25\\text{ N}$). The {{clamp}} function limits the commanded thrust to the physical range $[0, T_{max}]$. When the requested command lies outside this range, the actuator is {{saturation|saturated}} (indicated by shaded regions on the thrust plot).',
            '**Thrust deviation** ($\\term{u}{u}$): The difference between actual applied thrust and the hover baseline $T_0$.',
          ],
        },
        {
          type: 'p',
          text: 'The baseline prediction $T_{ff}$ can operate in three distinct modes:',
        },
        {
          type: 'table',
          caption: 'Feedforward modes',
          headers: ['Mode', 'Predicted thrust', 'Use'],
          rows: [
            ['Off', '$T_{ff} = 0$', 'Manual throttle or feedback must supply 100% of the force needed to fight gravity.'],
            ['Operating point (default)', '$T_{ff} = T_0 = m g + k(z_0 - z_s)$', 'Predicts the exact thrust needed to hover motionless at $z_0$ if the internal mass model is correct.'],
            ['Target equilibrium', '$T_{ff} = m g + k(r - z_s)$', 'Predicts the thrust required to balance at a new target altitude $r$. (A static equilibrium prediction, not a dynamic trajectory feedforward).'],
          ],
        },
        {
          type: 'callout',
          tone: 'note',
          title: 'Model mismatch in feedforward',
          text: 'Feedforward relies entirely on the parameters programmed into the flight computer (such as estimated mass and spring stiffness). If the physical drone is heavier than expected or carries an unmodeled payload, the feedforward prediction will be wrong, and feedforward has no way of knowing!',
        },
      ],
    },
    {
      id: 'm2-diagram',
      title: 'Two paths into the rotors',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'Flight control engineers rely on block diagrams to map the flow of signals and see where control decisions originate. Tracing the signal paths through the flight system reveals the fundamental difference between open-loop prediction and closed-loop feedback.',
        },
        { type: 'widget', widget: 'block-diagram' },
        {
          type: 'list',
          items: [
            '**The Feedforward Path (Open Loop)**: Signal flows directly from the target reference and internal model straight to the motor command. It **never reads the altimeter**. If an unexpected wind gust pushes the vehicle down, feedforward is completely blind to it and will not change its command.',
            '**The Feedback Path (Closed Loop)**: Signal begins at the physical {{sensor}} (altimeter), compares the measured altitude against the target reference to compute the current {{error}} ($e = r - y_m$), and calculates a corrective adjustment ($u_{fb}$). Feedback is the only path that detects and corrects surprises that were missing from the model.',
          ],
        },
      ],
    },
    {
      id: 'm2-predict',
      title: 'Predict',
      phase: 'predict',
      blocks: [
        {
          type: 'p',
          text: 'To understand why feedback is necessary, test your intuition on what happens when an unexpected disturbance hits an aircraft running on feedforward alone.',
        },
        {
          type: 'predict',
          id: 'm2-p1',
          prompt:
            'The tethered vehicle hovers at 2 m on feedforward $T_0$ alone. At 3 s a 1 N downward load appears. What happens by the end of the run?',
          options: [
            { id: 'back', text: 'It dips, then returns to 2 m.' },
            { id: 'lower', text: 'It settles at a new, lower altitude.' },
            { id: 'ground', text: 'It falls to the ground.' },
          ],
          reveal:
            'Because feedforward does not read the altimeter, motor thrust remains stuck at 9.81 N. With 1 N of extra downward weight, the drone drops. As it sinks below 2 m, the tether spring stretches upward and provides restoring force. It settles at a lower altitude where spring tension balances the extra 1 N ($k \\cdot \\Delta z = 1\\text{ N} \\implies \\Delta z = 0.5\\text{ m}$ drop).',
        },
      ],
    },
    {
      id: 'm2-lab',
      title: 'Prediction, hand correction, feedback',
      phase: 'experiment',
      blocks: [
        {
          type: 'p',
          text: 'In this lab, you will compare three fundamental control paradigms under an identical flight challenge: a stationary hover at 2 m that encounters an unexpected 1 N downward payload disturbance at $t = 3\\text{ s}$.',
        },
        {
          type: 'h',
          text: 'Part 1: Open-Loop Model Prediction (Feedforward Only)',
        },
        {
          type: 'p',
          text: '**Purpose**: Observe the vulnerability of pure feedforward. Without sensors, the flight computer assumes the vehicle is operating in an ideal world. When an unmodeled force acts on the craft, feedforward cannot detect or counteract the disturbance, leaving the vehicle permanently displaced.',
        },
        {
          type: 'h',
          text: 'Part 2: Human-in-the-Loop Feedback (Manual Correction)',
        },
        {
          type: 'p',
          text: '**Purpose**: Act as the biological feedback sensor and controller. You will watch altitude deviation on screen and use the manual correction slider to adjust thrust. This demonstrates human reaction lag, overshoot, and the cognitive burden of continuous manual stabilization.',
        },
        {
          type: 'h',
          text: 'Part 3: Automated Closed-Loop Feedback (Proportional Control)',
        },
        {
          type: 'p',
          text: '**Purpose**: See how automated feedback closes the loop. An algorithm measures sensor altitude, calculates tracking error, and automatically computes corrective thrust ($u_{fb}$) in real time, rapidly counteracting disturbances without human latency.',
        },
        { type: 'lab' },
      ],
    },
    {
      id: 'm2-inspect',
      title: 'Inspect',
      phase: 'inspect',
      blocks: [
        {
          type: 'p',
          text: 'Use the telemetry tools to compare how each control strategy handled the unexpected disturbance:',
        },
        {
          type: 'list',
          items: [
            'Click **Compare** in the simulator to overlay the altitude graphs of all three runs. Because the disturbance scenario is identical, differences in flight path come entirely from the control approach.',
            'In the open-loop run, verify on the thrust plot that requested thrust remained completely flat after 3 s despite the altitude drop.',
            'In the automated feedback run, expand **Controller contributions** to watch how $u_{fb}$ automatically grew in response to error.',
          ],
        },
      ],
    },
    {
      id: 'm2-explain',
      title: 'Explain',
      phase: 'explain',
      blocks: [
        { type: 'reflect', id: 'm2-r1', prompt: 'What did the feedforward method miss? How did the feedback help identify the disturbance and correct it?' },
      ],
    },
  ],
  lab: {
    id: 'm2-lab',
    version: 1,
    instructions: 'Complete all three 12 s runs to compare open-loop feedforward, manual human feedback, and automated feedback under an unexpected 1 N load at t = 3 s.',
    steps: [
      {
        id: 'm2-open',
        title: 'Part 1: Feedforward only',
        presetId: 'open-loop-load',
        instructions:
          'Load the step and run the 12 s simulation to the end. Watch the 1 N load hit at 3 s: because feedforward lacks sensor feedback, thrust remains completely flat at 9.81 N and the craft sags permanently to ~1.5 m.',
        permitted: [],
        requires: 'A complete 12 s run.',
      },
      {
        id: 'm2-manual',
        title: 'Part 2: Correct by hand',
        presetId: 'manual-correction',
        instructions:
          'Load and press Play (0.5× speed recommended). When the 1 N load drops at 3 s, use the Manual correction slider to boost thrust and guide the craft back toward 2 m; notice human latency and overshooting.',
        permitted: [],
        check: (r) => (r.manualEvents.some((e) => e.step * r.config.dt > 3) ? null : 'Make at least one manual correction after the load appears at 3 s.'),
        requires: 'A complete run with at least one manual correction after 3 s.',
      },
      {
        id: 'm2-feedback',
        title: 'Part 3: Supplied feedback',
        presetId: 'supplied-feedback',
        instructions:
          'Load and run to the end (12 s). Watch the automated feedback controller immediately detect the altitude dip at 3 s and automatically inject corrective thrust ($u_{fb}$) to hold the craft near 2 m.',
        permitted: [],
        requires: 'A complete 12 s run.',
      },
    ],
  },
  gate: {
    id: 'g2',
    version: 1,
    title: 'Check: feedforward and feedback',
    questions: [
      {
        id: 'g2-q1',
        version: 1,
        kind: 'match',
        binds: [],
        prompt: () => 'Match each signal to its role in the block diagram.',
        items: [
          { id: 'ff', text: 'Thrust predicted from the model, $T_{ff}$' },
          { id: 'fb', text: 'Correction computed from the error, $u_{fb}$' },
          { id: 'ym', text: 'Altimeter reading, $y_m$' },
          { id: 'd', text: 'Unexpected load, $d$' },
        ],
        choices: [
          { id: 'ffpath', text: 'feedforward path' },
          { id: 'fbpath', text: 'feedback path' },
          { id: 'meas', text: 'measurement (sensor output)' },
          { id: 'dist', text: 'disturbance input' },
        ],
        correct: () => ({ ff: 'ffpath', fb: 'fbpath', ym: 'meas', d: 'dist' }),
        hint: 'Which one never looks at the altimeter?',
        explanation: 'Feedforward predicts, the sensor measures, feedback corrects, and the disturbance is what nobody planned for.',
        review: 'm2-diagram',
      },
      {
        id: 'g2-q2',
        version: 1,
        kind: 'numeric',
        binds: ['m2-open'],
        prompt: () => 'From your feedforward-only run: how far did the altitude drop between just before the load and the end of the run?',
        table: (ctx) => ({
          caption: 'Samples from your run',
          headers: ['t (s)', 'z (m)', 'T_cmd (N)', 'd (N)'],
          rows: [before(ctx), after(ctx)].map((s) => [f(s.t), f(s.z, 3), f(s.Tcmd), f(s.d)]),
        }),
        unit: 'm',
        answer: (ctx) => before(ctx).z - after(ctx).z,
        tolerance: 0.005,
        misconceptions: [{ test: (x, ctx) => Math.abs(x + (before(ctx).z - after(ctx).z)) < 0.005, feedback: 'Report the drop as a positive distance.' }],
        hint: 'Subtract the final altitude from the altitude just before 3 s.',
        explanation: 'About 0.5 m. The spring, not the rotors, ended up carrying the load.',
        review: 'm2-inspect',
      },
      {
        id: 'g2-q3',
        version: 1,
        kind: 'choice',
        binds: ['m2-open'],
        prompt: (ctx) =>
          `In that run the requested thrust was ${f(before(ctx).Tcmd)} N before the load and ${f(after(ctx).Tcmd)} N at the end. Why did the unchanged predicted thrust fail after the load appeared?`,
        options: [
          { id: 'model', text: 'The model behind $T_{ff}$ did not include the load, and nothing measured the altitude to notice it.' },
          { id: 'wrong', text: '$T_{ff}$ was calculated incorrectly.', feedback: 'Before 3 s it held 2 m exactly. The prediction was right for the situation it was designed for.' },
          { id: 'gravity', text: 'Gravity got stronger.', feedback: 'Gravity was constant. The disturbance d changed.' },
          { id: 'spring', text: 'The spring pushed the vehicle down.', feedback: 'The spring helped hold the load. It pushes up once the vehicle is below $z_s$.' },
        ],
        correct: () => 'model',
        hint: 'What information would a controller need to respond to the load?',
        explanation: 'Feedforward only acts on what the model predicts. Surprises need feedback.',
        review: 'm2-diagram',
      },
      {
        id: 'g2-q4',
        version: 1,
        kind: 'choice',
        binds: [],
        prompt: () => 'Which measurement did the feedback controller need in order to correct the load?',
        options: [
          { id: 'alt', text: 'Altitude (from the altimeter)' },
          { id: 'thrust', text: 'Thrust', feedback: 'Thrust is what the controller outputs. It does not reveal the load.' },
          { id: 'load', text: 'The size of the load itself', feedback: 'If we measured the load we would feed it forward, but usually we can’t. Feedback works without knowing it by watching the altitude.' },
          { id: 'mass', text: 'Mass', feedback: 'Mass does not change during a run.' },
        ],
        correct: () => 'alt',
        hint: 'Look at the block diagram: what feeds the error?',
        explanation: 'Error = target − measured altitude, so altitude is the measurement feedback needs.',
        review: 'm2-diagram',
      },
      {
        id: 'g2-q5',
        version: 1,
        kind: 'numeric',
        binds: ['m2-feedback'],
        prompt: () => 'How much error $e = r - z$ remained at the end of your supplied-feedback run? Compare it to the open-loop drop.',
        table: (ctx) => {
          const s = lastSample(ctx, 'm2-feedback');
          return { caption: 'Final sample', headers: ['t (s)', 'r (m)', 'z (m)'], rows: [[f(s.t), f(s.r, 3), f(s.z, 3)]] };
        },
        unit: 'm',
        answer: (ctx) => {
          const s = lastSample(ctx, 'm2-feedback');
          return s.r - s.z;
        },
        tolerance: 0.003,
        misconceptions: [{ test: (x, ctx) => { const s = lastSample(ctx, 'm2-feedback'); return Math.abs(x + (s.r - s.z)) < 0.003; }, feedback: 'Error is target minus altitude, r − z. Below target means positive error.' }],
        hint: '$e = r - z$ from the table.',
        explanation: 'Feedback cut the sag from 0.5 m to about 0.05 m. Module 6 shows how to remove the rest.',
      },
    ],
  },
};
